import React, { useMemo, useState } from 'react';
import {
  ButtonToolbar,
  DropdownButton,
  FormControl,
  MenuItem,
} from 'react-bootstrap';
import styles from './StakeholderSearchSelect.module.css';

function StakeholderSearchSelect({
  stakeholders,
  selectedStakeholders,
  onSelectStakeholder,
  onClearAll,
  disabled = false,
  showArchived = false,
  disabledStakeholderIds = [],
  title = 'Select Stakeholder(s)',
  style = {},
  className = '',
  combined = false,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selectedCount = Object.keys(selectedStakeholders).length;

  const handleSelect = (stakeholder) => {
    onSelectStakeholder(stakeholder);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleToggle = (open) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const filteredStakeholders = useMemo(() => {
    if (!stakeholders) return [];

    let filtered = stakeholders;

    if (!showArchived) {
      filtered = filtered.filter((stakeholder) => !stakeholder.archived);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (stakeholder) =>
          (stakeholder.fullName &&
            stakeholder.fullName.toLowerCase().includes(searchLower)) ||
          (stakeholder.name &&
            stakeholder.name.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [stakeholders, searchTerm, showArchived]);

  const renderMenuItem = (stakeholder) => {
    const isSelected = selectedStakeholders[stakeholder.id];
    const isDisabled = disabledStakeholderIds.includes(stakeholder.id);
    const displayName = stakeholder.fullName || stakeholder.name || '';
    const archivedLabel = stakeholder.archived ? ' (archived)' : '';

    return (
      <MenuItem
        key={stakeholder.id}
        active={isSelected}
        disabled={isDisabled}
        onSelect={() => !isDisabled && handleSelect(stakeholder)}
      >
        <span
          className={stakeholder.archived && !isSelected ? 'text-muted' : ''}
        >
          {displayName}
          {archivedLabel}
        </span>
      </MenuItem>
    );
  };

  const renderStakeholdersByType = () => {
    if (!combined) {
      return filteredStakeholders.map(renderMenuItem);
    }

    const groupStakeholders = filteredStakeholders.filter(
      (s) => s.objectType === 'group'
    );
    const individualStakeholders = filteredStakeholders.filter(
      (s) => s.objectType === 'individual'
    );

    return (
      <React.Fragment>
        {groupStakeholders.length > 0 && (
          <React.Fragment>
            <MenuItem header style={{ padding: '3px 20px', fontSize: '10px' }}>
              Group
            </MenuItem>
            {groupStakeholders.map(renderMenuItem)}
          </React.Fragment>
        )}
        {individualStakeholders.length > 0 && (
          <React.Fragment>
            <MenuItem header style={{ padding: '3px 20px', fontSize: '10px' }}>
              Individual
            </MenuItem>
            {individualStakeholders.map(renderMenuItem)}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.stakeholderSearchSelect}>
      <ButtonToolbar>
        <DropdownButton
          bsStyle="info"
          id="stakeholder-search-select"
          title={title || `${selectedCount} selected`}
          className={`btn-block ${className}`}
          style={style}
          disabled={disabled}
          pullRight
          open={isOpen}
          onToggle={handleToggle}
        >
          <MenuItem header>
            <FormControl
              type="text"
              placeholder="Search stakeholders..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ margin: '5px 10px', width: 'calc(100% - 20px)' }}
            />
          </MenuItem>
          <MenuItem divider />
          {onClearAll && (
            <React.Fragment>
              <MenuItem active={selectedCount === 0} onSelect={onClearAll}>
                All
              </MenuItem>
              <MenuItem divider />
            </React.Fragment>
          )}
          {filteredStakeholders.length > 0 ? (
            renderStakeholdersByType()
          ) : (
            <MenuItem disabled>
              <span className="text-muted">No stakeholders found</span>
            </MenuItem>
          )}
        </DropdownButton>
      </ButtonToolbar>
    </div>
  );
}

export default StakeholderSearchSelect;
